import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { getPageTitle } from "../../utils/getPageTitle";

import Button from "../../components/common/button";
import UserData from "../../components/userData";
import UserModal from "../../components/modal/userModal";
import SearchItem from "../../components/searchItem";
import Pagination from "../../components/common/pagination";

import { useViewUserQuery } from "../../hooks/users/viewUserList";
import { useUserStore } from "../../hooks/users/createUser";
import { useUserUpdate } from "../../hooks/users/editUser";
import { useUserDelete } from "../../hooks/users/deleteUser";
import { useViewSubscription } from "../../hooks/subscription/subscription.view";
import {
  getPlanCreationLimits,
  getPlanLimitMessage,
} from "../subscription/planLimits";
import DeleteModal from "../../components/modal/deleteModal";
import { toast } from "react-hot-toast";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";

const Users = () => {
  const location = useLocation();
  const queryClient = useQueryClient();

  // =========================
  // STATES
  // =========================
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localUsers, setLocalUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [mode, setMode] = useState("create");
  const [modalKey, setModalKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [page, setPage] = useState(1);

  // =========================
  // API CALLS
  // =========================
  // const { data: users, isLoading: isListLoading, isError } = useViewUserQuery();
  const {
    data: usersResponse,
    isLoading: isListLoading,
    isFetching,
    isError,
  } = useViewUserQuery({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
  });
  const { mutateAsync: addUser, isPending: isAdding } = useUserStore();

  const { mutateAsync: updateUser, isPending: isUpdating } = useUserUpdate();

  const { mutateAsync: deleteUser, isPending: isDeleting } = useUserDelete();
  const { data: subscriptionResponse, isPlaceholderData } = useViewSubscription();
  const planLimits = getPlanCreationLimits(subscriptionResponse, {
    isReady: !isPlaceholderData,
  });

  // =========================
  // SET USERS
  // =========================
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!usersResponse) return;

    setLocalUsers(usersResponse.users ?? []);

    const apiPagination = usersResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [usersResponse]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);
  // =========================
  // ADD USER
  // =========================
  const handleAddUser = () => {
    if (!planLimits.users.canCreate) {
      toast.error(getPlanLimitMessage("users", planLimits));
      return;
    }

    setSelectedUser(null);
    setMode("create");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  // =========================
  // EDIT USER
  // =========================
  const handleEdit = (user) => {
    setSelectedUser(user);
    setMode("edit");
    setIsModalOpen(true);
  };

  // =========================
  // DELETE USER
  // =========================
  const handleDelete = (user) => {
    setSelectedUser(user);
    setMode("delete");
    setIsModalOpen(true);
  };

  // =========================
  // SAVE USER
  // =========================
  const handleSave = async (formData) => {
    setIsModalOpen(false);

    let tempUserId = null;

    try {
      if (mode === "create") {
        const imageFile = formData.get("profilePicture");

        const tempUser = {
          _id: Date.now().toString(),
          name: formData.get("name"),
          email: formData.get("email"),
          phone: formData.get("phone"),
          country: formData.get("country"),
          designation: formData.get("designation"),
          profilePicture: imageFile ? URL.createObjectURL(imageFile) : "",
        };
        tempUserId = tempUser._id;

        setLocalUsers((prev) => [tempUser, ...prev]);
        await addUser(formData);
        toast.success("User invited successfully");
      }

      if (mode === "edit" && selectedUser) {
        await updateUser({
          id: selectedUser._id,
          body: formData,
        });
        toast.success("User updated successfully");
      }

      if (mode === "delete") {
        await deleteUser(selectedUser._id);
        toast.success("User deleted successfully");
      }

      await queryClient.invalidateQueries({
        queryKey: ["usersList"],
      });

      setSelectedUser(null);
    } catch (error) {
      if (tempUserId) {
        setLocalUsers((prev) => prev.filter((user) => user._id !== tempUserId));
      }
      toast.error(error?.response?.data?.message || "Failed to save user");
    }
  };

  // =========================
  // LOADING
  // =========================
  if (isListLoading) {
    return <div>Loading...</div>;
  }

  // =========================
  // ERROR
  // =========================
  if (isError) {
    return <div>Error loading users</div>;
  }

  // =========================
  // UI
  // =========================
  return (
    <>
      {/* HEADER */}
      <div className="page-body">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>

          <p>User Management</p>
        </div>

        <div className="page-body-actions">
          <Button
            onClick={handleAddUser}
            disabled={!planLimits.users.canCreate}
          >
            + Invite Users
          </Button>
          <p
            className={`plan-limit-hint${planLimits.users.canCreate ? "" : " is-limit"}`}
          >
            {getPlanLimitMessage("users", planLimits)}
          </p>
        </div>
      </div>

      {/* SEARCH + PAGE SIZE */}
      <div className="list-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localUsers}
            searchField={["name", "email", "designation", "country"]}
            placeholder="Search users..."
            wrapSection={false}
            onResultsChange={({ searchItem }) => {
              setSearchTerm(searchItem);
            }}
          />

          <label className="list-toolbar-page-size">
            <span>Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>
      </div>

      {/* MODAL */}
      <UserModal
        key={
          mode === "create"
            ? `create-${modalKey}`
            : `edit-${selectedUser?._id || selectedUser?.id}`
        }
        isOpen={isModalOpen && mode !== "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        mode={mode}
        onSave={handleSave}
        isLoading={isAdding || isUpdating}
      />

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={() => handleSave()}
        title="Delete User"
        itemName={selectedUser?.name}
        isLoading={isDeleting}
      />

      {/* TABLE */}
      <UserData data={localUsers} onEdit={handleEdit} onDelete={handleDelete} />

      {isFetching && !isListLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default Users;
